import { z } from "zod";

export const integrationFeatureScheduleSchema = z.object({
  scheduleName: z.string(),
  cronExpression: z.string(),
  metadata: z.record(z.unknown()).optional(),
  createdDate: z.number().optional(),
  updatedDate: z.number().optional(),
});

export const integrationFeatureSchema = z.object({
  featureName: z.string(),
  metadata: z.record(z.unknown()).optional(),
  featureConfig: z
    .object({
      regions: z.union([z.string(), z.array(z.string())]).transform((val) => {
        if (Array.isArray(val)) {
          return val.join(",");
        }
        return val;
      }),
    })
    .nullish(),
  schedules: z.array(integrationFeatureScheduleSchema).optional(),
});

export const integrationSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  integrationType: z.string(),
  isPrivateApp: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
  connectionStatus: z.enum(["VALID", "INVALID", "UNKNOWN"]),
  features: z.array(integrationFeatureSchema).optional(),
  createdDate: z.number().optional(),
  updatedDate: z.number().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const integrationsResponseSchema = z.object({
  integrations: z.array(integrationSchema),
  nextPageToken: z.string().optional(),
});

export const integrationCredentialsSchema = z.object({
  type: z.string(),
  data: z
    .object({
      roleArn: z.string().optional(),
      externalId: z.string().optional(),
      service_account_json: z.string().optional(),
      clientId: z.string().optional(),
      clientSecret: z.string().optional(),
    })
    .catchall(z.unknown()),
  metadata: z
    .object({
      description: z.string().optional(),
      domain: z.string().optional(),
      dataverseUrl: z.string().optional(),
      baseUrl: z.string().optional(),
    })
    .optional(),
});

export const integrationTypeFeatureSchema = z.object({
  featureId: z.number(),
  featureName: z.string(),
  description: z.string(),
  isEnabled: z.boolean(),
});

export const integrationTypeFeaturesResponseSchema = z.object({
  integrationType: z.string(),
  features: z.array(integrationTypeFeatureSchema),
});

export const testConnectionResponseSchema = z.object({
  status: z.enum(["VALID", "INVALID", "UNKNOWN"]),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

export const featureResourceTypeSchema = z.object({
  id: z.number(),
  resourceTypeCode: z.string(),
  resourceTypeName: z.string(),
  serviceName: z.string(),
  description: z.string(),
  isActive: z.boolean(),
  requiredPermissions: z.record(z.unknown()),
});

export const featureResourceTypesResponseSchema = z.object({
  resourceTypes: z.array(featureResourceTypeSchema),
});

export type IntegrationTypeFeature = z.infer<
  typeof integrationTypeFeatureSchema
>;
export type IntegrationTypeFeaturesResponse = z.infer<
  typeof integrationTypeFeaturesResponseSchema
>;
export type TestConnectionResponse = z.infer<
  typeof testConnectionResponseSchema
>;
export type FeatureResourceType = z.infer<typeof featureResourceTypeSchema>;
export type FeatureResourceTypesResponse = z.infer<
  typeof featureResourceTypesResponseSchema
>;

// Use z.output to get the type after transformations (e.g., regions: string | string[] -> string)
export type Integration = z.output<typeof integrationSchema>;
export type IntegrationsResponse = z.output<typeof integrationsResponseSchema>;
export type IntegrationCredentials = z.infer<
  typeof integrationCredentialsSchema
>;
